import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGetTelephonySettingsQuery, useUpdateBusyTeammateRingingMutation } from "@/store/rest_api";
import { BusyTeammateRinging } from "@/model/interface/TelephonySettings";
import { apiErrorMessage } from "@/util/apiError";

export default function BusyTeammatesCard() {
  const { data: settings, isLoading } = useGetTelephonySettingsQuery();
  const [updateBusyTeammateRinging, { isLoading: isSaving }] = useUpdateBusyTeammateRingingMutation();
  const [ringing, setRinging] = useState<BusyTeammateRinging>("SKIP");

  async function handleChange(next: BusyTeammateRinging) {
    const previous = ringing;
    setRinging(next);
    try {
      await updateBusyTeammateRinging({ value: next }).unwrap();
      toast.success("Setting updated");
    } catch (error: unknown) {
      setRinging(previous);
      toast.error(apiErrorMessage(error, "Failed to update setting"));
    }
  }

  useEffect(() => {
    if (settings) {
      setRinging(settings.busyTeammateRinging);
    }
  }, [settings]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Teammates already on a call</CardTitle>
        <CardDescription>
          Decide what call flows, team transfers and AI transfers do when the person they would ring is busy.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-row items-start justify-between gap-4 rounded-lg border p-4">
          <div className="space-y-1 pr-4">
            <p className="text-base font-medium">When a teammate is on a call</p>
            <p className="text-sm text-muted-foreground">
              Skip them: the call goes to the next person, or to the step's No answer branch. Ring them as call
              waiting: the new call rings on top of the one they are on. A transfer to one specific person always
              rings them.
            </p>
          </div>
          <Select
            value={ringing}
            disabled={isLoading || isSaving}
            onValueChange={(value) => handleChange(value as BusyTeammateRinging)}
          >
            <SelectTrigger className="w-[230px] shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="SKIP">Skip them</SelectItem>
              <SelectItem value="CALL_WAITING">Ring them as call waiting</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}
